import { IGetMessengerConversationsResponse } from "@faire/web-api--source/indigofair/data/IGetMessengerConversationsResponse";
import { createContext, useCallback, useContext, useMemo } from "react";

import { MessengerStore } from "./MessengerStore";

interface MessengerStoreContext {
  store?: MessengerStore;
}

const MessengerStoreContext = createContext<MessengerStoreContext>({});

export const MessengerStoreContextProvider = MessengerStoreContext.Provider;

export const useMessengerStore = () => {
  const { store } = useContext(MessengerStoreContext);
  if (!store) {
    // eslint-disable-next-line no-console
    console.warn("MessengerStore not initialized in useMessengerStore");
  }
  return store!;
};

export const useMessengerStoreOrUndefined = () => {
  const { store } = useContext(MessengerStoreContext);
  return store;
};

const getUseFields = <T extends (keyof MessengerStore)[]>(
  _: T
): (() => Pick<MessengerStore, T[number]>) => {
  return useMessengerStore;
};

// Conversation List Hooks
export const useMessengerStoreConversationList = getUseFields([
  "orderedConversationIdentifiers",
  "hasConversations",
  "isLoadingConversations",
  "unreadConversationCount",
  "setTotalUnreadConversations",
  "starredByBrandConversationCount",
  "showOnlyUnreadConversations",
  "showOnlyStarredByBrandConversations",
  "setShowOnlyUnreadConversations",
  "setShowOnlyStarredByBrandConversations",
  "setSearch",
  "nextConversations",
  "latestUnreadConversationOtherPartyFirstName",
  "query",
  "conversationsNextRequest",
]);

export const useMessengerStoreInit = getUseFields(["init"]);

// Current Conversation Hooks
export const useMessengerStoreCurrentConversation = getUseFields([
  "hasCurrentConversation",
  "currentConversationIdentifier",
  "currentConversationToken",
  "currentConversationKey",
  "currentConversationOtherPartyUserToken",
  "currentConversationOtherPartyBrandToken",
  "currentConversationOtherPartyRetailerToken",
  "currentConversationOtherParty",
  "currentConversationUserToken",
  "currentConversationBrand",
  "currentConversationRetailer",
  "currentConversationOtherPartyBrandToken",
  "currentConversationOtherPartyProfileImage",
  "currentConversationOtherPartyCompanyName",
  "currentConversationOtherPartyBusinessImage",
  "currentConversationLatestMessage",
  "currentConversationOrderedMessages",
  "currentConversationIsUnread",
  "currentConversationIsInputDisabled",
  "currentConversationIsMarkedAsSpam",
  "currentConversationIsOtherPartyBrandCustomer",
  "currentConversationIsStarredByBrand",
  "setCurrentConversation",
  "updateCurrentConversationFromIdentifier",
  "sendMessageInCurrentConversation",
  "currentConversationFetchAdditionalMessagesThrottled",
  "currentConversationOtherPartyUserType",
  "currentConversationParticipants",
  "currentConversationBrand",
  "currentConversationRetailer",
  "currentConversationOtherPartyFirstName",
  "currentConversationOtherPartyCompanyName",
  "currentConversationShouldShowLoadingSpinner",
  "currentConversationFetchBrandCustomer",
  "currentConversationOpenConversation",
  "currentConversationMessageAuthors",
]);

// Conversation Management Hooks
export const useMessengerStoreConversationManagement = getUseFields([
  "setConversationIsStarredByBrand",
  "toggleConversationIsStarredByBrand",
  "hideConversation",
  "fetchConversationMessages",
  "sendMessageInConversation",
  "firstFetchOrUpdateConversations",
]);

// Context Management Hooks
export const useMessengerStoreUser = getUseFields(["isBrandUser"]);
export const useMessengerStoreBrandContext = getUseFields([
  "setCurrentBrandContext",
]);
export const useMessengerStoreRetailerContext = getUseFields([
  "currentRetailerContext",
  "updateCurrentRetailerContext",
]);

// Individual Conversation Hooks
export const useMessengerStoreGetConversation = getUseFields([
  "getConversationCreatedAt",
  "getConversationLatestMessage",
  "getConversationRetailer",
  "getConversationBrand",
  "getConversationToken",
  "getConversationOtherPartyType",
  "getConversationOtherPartyUserToken",
  "getConversationOtherPartyRetailerToken",
  "getConversationOtherPartyBrandToken",
  "getConversationIsUnread",
  "getConversationOtherPartyFirstName",
  "getConversationOtherPartyBusinessImage",
  "getConversationIsSupportConversation",
  "getConversationIsMarkedAsSpam",
  "getConversationIsStarredByBrand",
  "getConversationOtherPartyCompanyName",
  "isConversationToken",
  "hasConversationWithRetailer",
  "hasConversationWithBrand",
]);

export const useMessengerStoreLatestUnreadPopUpConversation = getUseFields([
  "latestUnreadPopUpConversationConversationToken",
  "latestUnreadPopUpConversationOtherPartyUserToken",
  "latestUnreadPopUpConversationOtherPartyBrandToken",
  "latestUnreadPopUpConversationOtherPartyRetailerToken",
  "hasUnreadPopUpConversation",
]);

export const useMessengerStoreGetConversationOrUndefined = () => {
  const store = useMessengerStoreOrUndefined();
  const hasConversationWithBrand = useCallback(
    (brandToken: string) => {
      return store?.hasConversationWithBrand(brandToken) ?? false;
    },
    [store]
  );

  return useMemo(
    () => ({ hasConversationWithBrand }),
    [hasConversationWithBrand]
  );
};

export const useMessengerStoreBrandContextOrUndefined = () => {
  const store = useMessengerStoreOrUndefined();
  const setCurrentBrandContext = useCallback(
    async (
      brand: IGetMessengerConversationsResponse.ISimpleBrand | undefined
    ) => {
      if (store) {
        return await store.setCurrentBrandContext(brand);
      }
      return undefined;
    },
    [store]
  );

  return useMemo(() => ({ setCurrentBrandContext }), [setCurrentBrandContext]);
};
